/**
 * ObjectivesSelectionStep Component
 *
 * Content-only component for selecting and ordering trading objectives during
 * the pre-trading ritual. Displays configured number of slots based on maxObjectives,
 * with drag-and-drop reordering where first slot = Primary objective.
 *
 * This component renders just the inputs (no Card wrapper) - the parent
 * component (PreTradingPhase) is responsible for providing the Card chrome.
 *
 * This is different from ObjectivesManagement (settings) which configures
 * the objective library and queue - this component lets users select TODAY's objectives.
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

// 2. Internal absolute imports
import {
  Card,
  CardBody,
  Button,
  TextArea,
  Chip,
  EmptyState,
  SortableList,
  DragHandle,
  Tooltip,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { Target, X, Plus, AlertTriangle, Trash2, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { tradingObjectiveService } from '@/services/TradingObjectiveService';
import { tradingObjectiveSettingsService } from '@/services/TradingObjectiveSettingsService';
import { noteDraftService } from '@/services/NoteDraftService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { generateUUID } from '@/utils/uuid';
import { isTombstoned } from '@/utils/tombstone';
import type { TradingObjective } from '@/types/schema/TradingObjective';
import type { ObjectiveType } from '@/types/schema/TradingObjectiveSettings';
import type { ObjectiveDraft } from '@/types/transient/ObjectiveDraft';
import type { NoteDraft } from '@/types/transient/NoteDraft';
import { ObjectiveReplacerModal } from './ObjectiveReplacerModal';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for ObjectivesSelectionStep component
 */
export interface ObjectivesSelectionStepProps {
  /** Maximum number of objectives allowed (from preTradingConfig snapshot) */
  maxObjectives: number;
  /** Current objectives selection (ObjectiveDraft array) */
  objectives: ObjectiveDraft[];
  /** Callback when objectives change */
  onObjectivesChange: (objectives: ObjectiveDraft[]) => void;
}

/**
 * Adapter type for SortableList (requires `id` property)
 * Uses ObjectiveDraft.id as the sortable ID
 */
interface SortableObjectiveItem {
  /** Unique ID for SortableList (same as ObjectiveDraft.id) */
  id: string;
  /** The full ObjectiveDraft */
  draft: ObjectiveDraft;
  /** The resolved TradingObjective (undefined if deleted) */
  objective: TradingObjective | undefined;
}

/**
 * Props for FilledSlotRenderer
 */
interface FilledSlotRendererProps {
  item: SortableObjectiveItem;
  dragHandleProps: DragHandleProps;
  isFirst: boolean;
  onRemove: (draftId: string) => void;
  onReplace: (draftId: string) => void;
  onNotesChange: (draftId: string, notes: string) => void;
  getTypeName: (typeId: string) => string;
  /** Current note text for this objective draft */
  noteText: string;
  /** Whether a note exists for this objective draft (for button label) */
  hasNote: boolean;
}

// =============================================================================
// Sub-Components
// =============================================================================

/**
 * Renders a filled objective slot with drag handle, info, notes, and remove button
 */
const FilledSlotRenderer: React.FC<FilledSlotRendererProps> = ({
  item,
  dragHandleProps,
  isFirst,
  onRemove,
  onReplace,
  onNotesChange,
  getTypeName,
  noteText,
  hasNote,
}) => {
  const { draft, objective } = item;
  const [isNotesExpanded, setIsNotesExpanded] = useState(hasNote);

  // Determine status indicators
  const isDeleted = !objective;
  const isInactive = objective && objective.status !== 'active';
  const typeName = objective?.type ? getTypeName(objective.type) : null;

  return (
    <Card
      shadow="sm"
      className={`${isFirst ? 'ring-1 ring-primary' : ''} ${isDeleted ? 'opacity-60' : ''}`}
      radius="md"
    >
      <CardBody className="flex flex-col gap-2 py-3 px-3">
        {/* Row 1: Drag handle + Info + Notes toggle + Remove */}
        <div className="flex flex-row items-center gap-2">
          <DragHandle dragHandleProps={dragHandleProps} className="flex-shrink-0" />

          <div className="flex-1 min-w-0">
            {/* Name + Type + Primary/Status badges */}
            <div className="flex flex-wrap items-center gap-2">
              {isDeleted ? (
                <span className="text-text-secondary italic truncate">
                  Deleted objective
                </span>
              ) : (
                <>
                  <span className="text-text-primary truncate">
                    {objective.name}
                  </span>
                  {typeName && (
                    <>
                      <span className="text-text-tertiary hidden sm:inline">•</span>
                      <span className="uppercase text-sm text-text-secondary truncate hidden sm:inline">
                        {typeName}
                      </span>
                    </>
                  )}
                </>
              )}
              {isFirst && (
                <Chip size="sm" color="primary" variant="flat">
                  Primary
                </Chip>
              )}
              {isInactive && (
                <Tooltip content={`This objective is ${objective.status}`}>
                  <Chip size="sm" color="warning" variant="flat" startContent={<AlertTriangle size={12} />}>
                    {objective.status}
                  </Chip>
                </Tooltip>
              )}
              {isDeleted && (
                <Tooltip content="This objective was deleted from the library">
                  <Chip size="sm" color="danger" variant="flat" startContent={<Trash2 size={12} />}>
                    Deleted
                  </Chip>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Notes toggle button */}
          <Button
            size="sm"
            variant="light"
            className="flex-shrink-0"
            startContent={isNotesExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            onPress={() => setIsNotesExpanded(!isNotesExpanded)}
          >
            <span className="hidden sm:inline">{hasNote ? 'Notes' : 'Add notes'}</span>
            <span className="sm:hidden">{hasNote ? 'Notes' : 'Notes'}</span>
          </Button>

          {/* Replace button */}
          <Tooltip content="Replace with different objective">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => onReplace(draft.id)}
              aria-label="Replace objective"
            >
              <RefreshCw size={16} />
            </Button>
          </Tooltip>

          {/* Remove button */}
          <Tooltip content="Remove objective">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => onRemove(draft.id)}
              aria-label="Remove objective"
            >
              <X size={16} />
            </Button>
          </Tooltip>
        </div>

        {/* Collapsible notes section */}
        {isNotesExpanded && (
          <div className="pl-8">
            <TextArea
              placeholder="Pre-session notes (optional)"
              value={noteText}
              onValueChange={(value) => onNotesChange(draft.id, value)}
              minRows={2}
              maxRows={4}
              classNames={{
                inputWrapper: 'min-h-[44px]',
                input: 'text-base',
              }}
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};

/**
 * Renders an empty slot placeholder with disabled "Add objective" button
 * (Button enabled in Phase 3 when Replace Modal is implemented)
 */
const EmptySlotRenderer: React.FC<{
  slotIndex: number;
  isFirst: boolean;
  onAdd: (slotIndex: number) => void;
  disabled: boolean;
}> = ({ slotIndex, isFirst, onAdd, disabled }) => {
  return (
    <Card
      shadow="sm"
      className={`border-2 border-dashed border-default-200 ${isFirst ? 'border-primary-300' : ''}`}
      radius="md"
    >
      <CardBody className="flex flex-row items-center justify-center gap-3 py-4 px-3">
        <Button
          size="sm"
          variant="flat"
          color={isFirst ? 'primary' : 'default'}
          startContent={<Plus size={16} />}
          onPress={() => onAdd(slotIndex)}
          isDisabled={disabled}
        >
          {isFirst ? 'Add Primary Objective' : 'Add Objective'}
        </Button>
      </CardBody>
    </Card>
  );
};

// =============================================================================
// Main Component
// =============================================================================

/**
 * ObjectivesSelectionStep Component
 *
 * Renders slot-based objective selection with drag-and-drop reordering.
 * First slot is always the Primary objective.
 */
export const ObjectivesSelectionStep: React.FC<ObjectivesSelectionStepProps> = ({
  maxObjectives,
  objectives,
  onObjectivesChange,
}) => {
  // Objective library state
  const [allObjectives, setAllObjectives] = useState<TradingObjective[]>([]);
  const [objectiveTypes, setObjectiveTypes] = useState<ObjectiveType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Notes state - keyed by ObjectiveDraft.id
  const [notesMap, setNotesMap] = useState<Record<string, NoteDraft | null>>({});

  // Refs for debounced note saving
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingNotesRef = useRef<Record<string, { text: string; noteId: string | null }>>({});
  const notesMapRef = useRef<Record<string, NoteDraft | null>>({});

  // Replace modal state
  const [showReplacerModal, setShowReplacerModal] = useState(false);
  const [pendingSlotIndex, setPendingSlotIndex] = useState<number | null>(null);

  // Objective lookup map for O(1) lookups
  const objectiveMap = useMemo(
    () => new Map(allObjectives.map((o) => [o.id, o])),
    [allObjectives]
  );

  // Transform objectives to SortableObjectiveItem for SortableList
  const sortableItems: SortableObjectiveItem[] = useMemo(
    () =>
      objectives.map((draft) => ({
        id: draft.id,
        draft,
        objective: objectiveMap.get(draft.objectiveId),
      })),
    [objectives, objectiveMap]
  );

  // Count of filled slots
  const filledCount = objectives.length;

  // Empty slots to render (maxObjectives - filled)
  const emptySlotCount = Math.max(0, maxObjectives - filledCount);

  // Derive selected objective IDs for duplicate prevention
  // For replace mode, exclude the objective being replaced
  const selectedObjectiveIds = useMemo(() => {
    const ids = new Set(objectives.map((o) => o.objectiveId));
    // If in replace mode, exclude the objective at that index
    if (pendingSlotIndex !== null && pendingSlotIndex < objectives.length) {
      const objectiveAtIndex = objectives[pendingSlotIndex];
      if (objectiveAtIndex) {
        ids.delete(objectiveAtIndex.objectiveId);
      }
    }
    return ids;
  }, [objectives, pendingSlotIndex]);

  // Derive existing tags for ObjectiveModal autocomplete
  const existingTags = useMemo(() => {
    const tags = new Set<string>();
    for (const obj of allObjectives) {
      for (const tag of obj.tags) {
        tags.add(tag);
      }
    }
    return Array.from(tags);
  }, [allObjectives]);

  // Helper to resolve type ID to name
  const getTypeName = useCallback(
    (typeId: string): string => {
      const type = objectiveTypes.find((t) => t.id === typeId);
      return type?.name ?? 'Unknown';
    },
    [objectiveTypes]
  );

  // Load objective library and types
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [loadedObjectives, settings] = await Promise.all([
        tradingObjectiveService.getAll(),
        tradingObjectiveSettingsService.getOrCreateDefaultSettings(),
      ]);
      setAllObjectives(loadedObjectives);
      setObjectiveTypes(settings.objectiveTypes);
    } catch (error) {
      console.error('Failed to load objectives:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to cross-tab changes for trading_objectives
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('trading_objectives', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Load notes for current objectives
  const loadNotes = useCallback(async () => {
    // Short-circuit if no objectives - prevents stale notes from prior session
    if (objectives.length === 0) {
      setNotesMap({});
      return;
    }

    try {
      // Build a single nextMap to avoid flicker from multiple setState calls
      const nextMap: Record<string, NoteDraft | null> = {};

      // Load notes for all objectives in parallel
      const notePromises = objectives.map(async (draft) => {
        const notes = await noteDraftService.getByOwner('objective', draft.id);

        // Dev-only warning for single-note invariant violation
        if (import.meta.env.DEV && notes.length > 1) {
          console.warn(
            `[ObjectivesSelectionStep] Multiple notes found for ObjectiveDraft ${draft.id}. ` +
            `Expected 0 or 1, got ${notes.length}. Using first note.`
          );
        }

        return { draftId: draft.id, note: notes[0] ?? null };
      });

      const results = await Promise.all(notePromises);
      for (const { draftId, note } of results) {
        nextMap[draftId] = note;
      }

      setNotesMap(nextMap);
    } catch (error) {
      console.error('Failed to load notes for objectives:', error);
    }
  }, [objectives]);

  // Load notes when objectives change
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (cancelled) return;
      await loadNotes();
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [loadNotes]);

  // Subscribe to cross-tab changes for note_drafts
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('note_drafts', () => {
      loadNotes();
    });
    return () => unsubscribe();
  }, [loadNotes]);

  // Keep notesMapRef in sync for use in debounced callbacks (avoids stale closure)
  useEffect(() => {
    notesMapRef.current = notesMap;
  }, [notesMap]);

  // Handle reorder from SortableList
  const handleReorder = useCallback(
    (reorderedItems: SortableObjectiveItem[]) => {
      // Map back to ObjectiveDraft array, updating isPrimary based on position
      const now = new Date().toISOString();
      const reorderedDrafts = reorderedItems.map((item, index) => ({
        ...item.draft,
        isPrimary: index === 0,
        updatedAt: now,
      }));
      onObjectivesChange(reorderedDrafts);
    },
    [onObjectivesChange]
  );

  // Handle remove objective
  const handleRemove = useCallback(
    (draftId: string) => {
      // Clear any pending save timer FIRST (prevents race condition)
      if (saveTimersRef.current[draftId]) {
        clearTimeout(saveTimersRef.current[draftId]);
        delete saveTimersRef.current[draftId];
      }
      // Remove from pendingNotesRef to prevent flush from re-adding
      delete pendingNotesRef.current[draftId];

      // Cascade delete: soft-delete associated note if exists (with valid ID)
      const existingNote = notesMapRef.current[draftId];
      if (existingNote?.id) {
        noteDraftService.softDelete(existingNote.id).catch((err) => {
          console.error('Failed to soft-delete note on objective remove:', err);
        });
      }
      // Optimistically remove from notesMap (even if no note or placeholder)
      setNotesMap((prev) => {
        const next = { ...prev };
        delete next[draftId];
        return next;
      });

      const now = new Date().toISOString();
      const filtered = objectives.filter((d) => d.id !== draftId);
      // Update isPrimary for new first item
      const updated = filtered.map((draft, index) => ({
        ...draft,
        isPrimary: index === 0,
        updatedAt: now,
      }));
      onObjectivesChange(updated);
    },
    [objectives, onObjectivesChange]
  );

  // Handle notes change - debounced save to NoteDraftService
  const handleNotesChange = useCallback(
    (draftId: string, text: string) => {
      // Capture noteId BEFORE optimistic update (so we have it for delete case)
      // Prefer existing pending noteId if notesMapRef is null (handles clear → retype within debounce)
      const existingPendingNoteId = pendingNotesRef.current[draftId]?.noteId;
      const currentNoteId = notesMapRef.current[draftId]?.id || existingPendingNoteId || null;

      // Store pending value with captured noteId
      pendingNotesRef.current[draftId] = { text, noteId: currentNoteId };

      // Clear existing timer for this draftId
      if (saveTimersRef.current[draftId]) {
        clearTimeout(saveTimersRef.current[draftId]);
      }

      // Optimistically update local notesMap for immediate UI feedback
      setNotesMap((prev) => {
        const existingNote = prev[draftId];
        if (text) {
          // Update or create optimistic note
          if (existingNote) {
            return { ...prev, [draftId]: { ...existingNote, text } };
          }
          // No existing note - will be created on save, for now show text in UI
          // Create a partial optimistic note (will be replaced with real one after add)
          return {
            ...prev,
            [draftId]: {
              id: '', // Placeholder - will be replaced
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              noteType: 'objective',
              ownerId: draftId,
              text,
              timestamp: new Date().toISOString(),
              captureContext: 'pre-trading',
            } as NoteDraft,
          };
        } else {
          // Text is empty - set to null optimistically (for delete case)
          return { ...prev, [draftId]: null };
        }
      });

      // Schedule debounced save (400ms)
      saveTimersRef.current[draftId] = setTimeout(async () => {
        const pending = pendingNotesRef.current[draftId];
        if (!pending) return; // Already cleared

        const { text: pendingText, noteId } = pending;

        // Tombstone guard: verify note still valid before update/delete
        const currentNote = notesMapRef.current[draftId];
        const noteStillValid = noteId && currentNote?.id === noteId && !isTombstoned(currentNote);

        try {
          if (noteId && pendingText && noteStillValid) {
            // Update existing note
            await noteDraftService.update(noteId, { text: pendingText });
          } else if (noteId && !pendingText) {
            // Delete note (text is empty) - no noteStillValid check needed, softDelete is idempotent
            await noteDraftService.softDelete(noteId);
            setNotesMap((prev) => ({ ...prev, [draftId]: null }));
          } else if ((!noteId || !noteStillValid) && pendingText) {
            // Create new note (handles both new notes and tombstoned notes)
            const now = new Date().toISOString();
            const newNote = await noteDraftService.add({
              noteType: 'objective',
              ownerId: draftId,
              text: pendingText,
              timestamp: now,
              captureContext: 'pre-trading',
            });
            setNotesMap((prev) => ({ ...prev, [draftId]: newNote }));
            // Update pending entry's noteId for subsequent edits
            if (pendingNotesRef.current[draftId]) {
              pendingNotesRef.current[draftId].noteId = newNote.id;
            }
          }
          // (!noteId || !noteStillValid) && !pendingText → no-op
        } catch (error) {
          console.error('Failed to save note:', error);
        } finally {
          // Clear pending
          delete pendingNotesRef.current[draftId];
          delete saveTimersRef.current[draftId];
        }
      }, 400);
    },
    [] // No dependencies - uses refs for current values
  );

  // Handle add objective - opens ObjectiveReplacerModal in add mode
  const handleAdd = useCallback(() => {
    setPendingSlotIndex(null); // null = add mode (append)
    setShowReplacerModal(true);
  }, []);

  // Handle replace objective - opens ObjectiveReplacerModal in replace mode
  const handleReplace = useCallback(
    (draftId: string) => {
      const index = objectives.findIndex((d) => d.id === draftId);
      if (index !== -1) {
        setPendingSlotIndex(index);
        setShowReplacerModal(true);
      }
    },
    [objectives]
  );

  // Handle objective selection from modal
  const handleSelectObjective = useCallback(
    (objective: TradingObjective) => {
      // Guard against already-selected (race condition with cross-tab sync)
      if (selectedObjectiveIds.has(objective.id)) {
        return;
      }

      // Ensure objective is in allObjectives (handles inline creation race condition)
      // This prevents the objective from showing as "Deleted" before sync completes
      setAllObjectives((prev) =>
        prev.some((o) => o.id === objective.id) ? prev : [...prev, objective]
      );

      // In replace mode, cascade delete the old objective's note
      if (pendingSlotIndex !== null && pendingSlotIndex < objectives.length) {
        const oldDraft = objectives[pendingSlotIndex];
        if (oldDraft) {
          const oldDraftId = oldDraft.id;

          // Clear any pending save timer FIRST (prevents race)
          if (saveTimersRef.current[oldDraftId]) {
            clearTimeout(saveTimersRef.current[oldDraftId]);
            delete saveTimersRef.current[oldDraftId];
          }
          // Remove from pendingNotesRef
          delete pendingNotesRef.current[oldDraftId];

          // Soft-delete associated note if exists (with valid ID)
          const existingNote = notesMapRef.current[oldDraftId];
          if (existingNote?.id) {
            noteDraftService.softDelete(existingNote.id).catch((err) => {
              console.error('Failed to soft-delete note on objective replace:', err);
            });
          }
          // Optimistically remove from notesMap (even if no note or placeholder)
          setNotesMap((prev) => {
            const next = { ...prev };
            delete next[oldDraftId];
            return next;
          });
        }
      }

      const now = new Date().toISOString();
      const newDraft: ObjectiveDraft = {
        id: generateUUID(),
        createdAt: now,
        updatedAt: now,
        objectiveId: objective.id,
      };

      let newObjectives: ObjectiveDraft[];

      if (pendingSlotIndex === null) {
        // Add mode: append to array
        newObjectives = [...objectives, newDraft];
      } else {
        // Replace mode: replace at index (do NOT preserve notes)
        newObjectives = objectives.map((draft, index) =>
          index === pendingSlotIndex ? newDraft : draft
        );
      }

      // Update isPrimary flags (first = true, rest = false)
      newObjectives = newObjectives.map((draft, index) => ({
        ...draft,
        isPrimary: index === 0,
        updatedAt: now,
      }));

      onObjectivesChange(newObjectives);
      setShowReplacerModal(false);
      setPendingSlotIndex(null);
    },
    [objectives, pendingSlotIndex, selectedObjectiveIds, onObjectivesChange]
  );

  // Handle modal close
  const handleCloseReplacerModal = useCallback(() => {
    setShowReplacerModal(false);
    setPendingSlotIndex(null);
  }, []);

  // Unmount cleanup: clear pending timers and refs
  useEffect(() => {
    return () => {
      // Clear all timers (prevents callbacks from firing after unmount)
      for (const timerId of Object.values(saveTimersRef.current)) {
        clearTimeout(timerId);
      }
      saveTimersRef.current = {};
      // Clear pending refs (prevents stale text if component remounts quickly)
      pendingNotesRef.current = {};
    };
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-text-secondary">Loading objectives...</p>
      </div>
    );
  }

  // Empty state when no objectives in library AND no existing selections
  // (If user has selections, render normal UI so they can see/remove them even if library is empty)
  if (allObjectives.length === 0 && objectives.length === 0) {
    return (
      <EmptyState
        icon={<Target size={24} />}
        title="No objectives available"
        description="Create objectives in Settings to select them for your session"
      />
    );
  }

  // No objectives selected warning
  const showValidationWarning = objectives.length === 0;

  return (
    <div className="space-y-4">
      {/* Validation warning */}
      {showValidationWarning && (
        <div className="rounded-lg bg-warning-50 border border-warning-200 p-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-warning-600 flex-shrink-0" />
          <span className="text-sm text-warning-700">
            Select at least one objective for your trading session
          </span>
        </div>
      )}

      {/* Filled slots with drag-and-drop */}
      {sortableItems.length > 0 && (
        <SortableList
          items={sortableItems}
          onReorder={handleReorder}
          className="space-y-3"
          renderItem={(item, dragHandleProps) => (
            <FilledSlotRenderer
              item={item}
              dragHandleProps={dragHandleProps}
              isFirst={item.id === sortableItems[0]?.id}
              onRemove={handleRemove}
              onReplace={handleReplace}
              onNotesChange={handleNotesChange}
              getTypeName={getTypeName}
              noteText={notesMap[item.draft.id]?.text ?? ''}
              hasNote={!!notesMap[item.draft.id]}
            />
          )}
        />
      )}

      {/* Empty slots */}
      {emptySlotCount > 0 && (
        <div className="space-y-3">
          {Array.from({ length: emptySlotCount }).map((_, index) => {
            const slotIndex = filledCount + index;
            const isFirst = slotIndex === 0;
            return (
              <EmptySlotRenderer
                key={`empty-${slotIndex}`}
                slotIndex={slotIndex}
                isFirst={isFirst}
                onAdd={handleAdd}
                disabled={false}
              />
            );
          })}
        </div>
      )}

      {/* ObjectiveReplacerModal */}
      <ObjectiveReplacerModal
        isOpen={showReplacerModal}
        onClose={handleCloseReplacerModal}
        onSelectObjective={handleSelectObjective}
        selectedObjectiveIds={selectedObjectiveIds}
        existingTags={existingTags}
      />
    </div>
  );
};

export default ObjectivesSelectionStep;
